import { ManageModal } from "@/components/ManageModal/ManageModal";
import { useManageModalConfig } from "@/components/ManageModal/manageConfig";
import { renderManagePane } from "@/components/ManageModal/managePanes";
import { ROUTES } from "@/constants/routes";
import { OpenSettingsProvider } from "@/context/OpenSettingsContext";
import { SettingsInDialogProvider } from "@/context/SettingsInDialogContext";
import { SettingsInitialNavigationProvider } from "@/context/SettingsInitialNavigationContext";
import { useSeatPurchase } from "@/hooks/useSeatPurchase";
import {
  normalizeOpenSettingsInput,
  resolveManageEntry,
  type OpenSettingsInput,
  type OpenSettingsState,
} from "@/utils/settingsNavigation";
import React, { useCallback, useMemo, useState } from "react";
import { useSettingsRouteSync } from "./useSettingsRouteSync";

interface LayoutSettingsShellProps {
  children: React.ReactNode;
}

/**
 * Mounts the Manage modal once per layout and provides `openSettings`.
 *
 * `openSettings(path)` is the one opening contract — every entry point names a
 * path and the shell's alias/landing maps pick the tab and scope. The full
 * path also rides the initial-navigation context so a pane can drill in
 * (a connector detail link lands on the Connectors tab, opened on that
 * connector).
 */
export function LayoutSettingsShell({ children }: LayoutSettingsShellProps) {
  // Seats are bought from two places; both mount this one picker.
  const { openSeatPicker, seatPicker } = useSeatPurchase(ROUTES.SETTINGS.USAGE);
  const config = useManageModalConfig({ onAddSeats: openSeatPicker });
  const [entry, setEntry] = useState<string | null>(null);
  const [initialNavigation, setInitialNavigation] = useState<OpenSettingsState>(
    {},
  );

  const openSettings = useCallback(
    (input?: OpenSettingsInput) => {
      const normalized = normalizeOpenSettingsInput(input);
      setInitialNavigation(normalized);
      const resolved = normalized.pathname
        ? resolveManageEntry(normalized.pathname, config.allMenuItems)
        : undefined;
      setEntry(resolved ?? config.defaultTab ?? null);
    },
    [config.allMenuItems, config.defaultTab],
  );

  const closeSettings = useCallback(() => {
    setEntry(null);
    setInitialNavigation({});
  }, []);

  useSettingsRouteSync(openSettings, entry !== null, closeSettings);

  // A pane consumes the deep-linked path once; switching tabs by hand must not
  // re-drill into it.
  const initialNavigationValue = useMemo(
    () => ({ pathname: initialNavigation.pathname }),
    [initialNavigation.pathname],
  );

  return (
    <OpenSettingsProvider value={openSettings}>
      {children}
      <SettingsInDialogProvider value={true}>
        <SettingsInitialNavigationProvider value={initialNavigationValue}>
          <ManageModal
            // Every trigger in the product says "Settings" — the gear, the
            // account-menu row, the deep links. The design system's shell
            // defaults its accessible name to "Manage" (its component name)
            // and expects the consumer to name it; announcing "Manage dialog"
            // to a screen reader after clicking Settings is a mismatch, and it
            // was called Settings before this refactor.
            title="Settings"
            tabs={config.tabs}
            rails={config.rails}
            aliases={config.aliases}
            landing={config.landing}
            tab={entry}
            onTab={(tab) => {
              setEntry(tab);
              setInitialNavigation({});
            }}
            onClose={closeSettings}
            renderPane={renderManagePane}
          />
          {seatPicker}
        </SettingsInitialNavigationProvider>
      </SettingsInDialogProvider>
    </OpenSettingsProvider>
  );
}
